import * as React from 'react';
import * as O from 'fp-ts/Option';
import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/lib/function';

import { Card } from '../../../components/Card';
import {
  FullMatchFragment,
  useMatchImagesQuery,
  useDeleteImageMutation,
  useSetMatchHighlightImageMutation,
} from '../../../generated/graphql';

import { imageSize } from '../../../utils/imagekit';
import { useAlert } from '../../../contexts/AlertContext';
import { CardHeading } from '../../../components/CardHeading';
import { PhotoOverlay } from '../../../components/PhotoOverlay';
import { isNil } from '../../../utils/isNil';

interface Props {
  match: Pick<FullMatchFragment, 'id' | 'team'>;
}

const MAX_IMAGES_PER_MATCH = 8;

// we might not need the match photo uploader so load it lazily
const LazyMatchPhotoUploader = React.lazy(() =>
  import('../components/MatchPhotoUploader').then(({ MatchPhotoUploader }) => ({
    default: MatchPhotoUploader,
  })),
);

export const MatchPhotosCard: React.FC<Props> = ({ match }) => {
  const [matchImagesRes, executeMatchImageQuery] = useMatchImagesQuery({
    variables: { id: match.id },
    requestPolicy: 'cache-and-network',
  });
  const [, executeDeleteImageMutation] = useDeleteImageMutation();
  const [, executeSetHighlightImage] = useSetMatchHighlightImageMutation();
  const alert = useAlert();
  const canUpload = match.team.isCaptain || !isNil(match.team.player);

  const [selectedImageIdx, setSelectedImageIdx] = React.useState<
    O.Option<number>
  >(O.none);

  const deleteImage = (imageId: string) => {
    return executeDeleteImageMutation({ id: imageId }).then((res) => {
      setSelectedImageIdx(O.none);
      if (res.data?.deleteImage?.validation || res.error) {
        alert(res.error?.message || 'Something went wrong', 'danger');
        return;
      }

      if (res.data?.deleteImage) {
        alert('Photo deleted');
        return executeMatchImageQuery({ requestPolicy: 'cache-and-network' });
      }
    });
  };

  return pipe(
    O.fromNullable(matchImagesRes.data?.match?.images),
    O.filter((images) => canUpload || A.isNonEmpty(images)),
    O.map((images) => {
      const setHighlightImage = (imageId: string) => {
        return executeSetHighlightImage({ imageId }).then((res) => {
          if (res.data?.setMatchHighlightImage?.entity) {
            alert('Highlight photo set');
            executeMatchImageQuery({ requestPolicy: 'cache-and-network' });
            setSelectedImageIdx(O.none);
            return;
          }
          alert('Something went wrong', 'danger');
        });
      };

      return (
        <Card key="1" className="space-y-4 bg-white">
          <CardHeading>Photos</CardHeading>
          {canUpload ? (
            <p className="text-xs text-grey-700">
              Up to {MAX_IMAGES_PER_MATCH} photos can be added to the match.
            </p>
          ) : null}
          <PhotoOverlay
            isAdmin={match.team.isOwner}
            images={images}
            selectedImageIdx={selectedImageIdx}
            selectImageIdx={setSelectedImageIdx}
            deleteImage={deleteImage}
            setHighlight={setHighlightImage}
          />
          <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
            {images.map((image, idx) => (
              <div
                className="aspect-w-1 aspect-h-1 rounded-md overflow-hidden"
                key={image.id}
              >
                <button
                  onClick={() => setSelectedImageIdx(O.some(idx))}
                  className="block w-full h-full"
                >
                  <img
                    src={imageSize(image.url, 'thumb')}
                    className="object-cover w-full h-full"
                  />
                </button>
              </div>
            ))}

            {canUpload && images.length < MAX_IMAGES_PER_MATCH ? (
              <React.Suspense key={match.id} fallback={null}>
                <LazyMatchPhotoUploader matchId={match.id} />
              </React.Suspense>
            ) : null}
          </div>
        </Card>
      );
    }),
    O.toNullable,
  );
};
